import React, { useEffect, useState } from "react";
import { ProviderConsumer, Flex, Input, SearchIcon, Card, Text, Checkbox, RadioGroup } from "@fluentui/react-northstar";
import { ITemplateFormProperties } from "./TicketTemplates";
import { AdaptiveCardElement } from "./AdaptiveCardElement";
import defaultCards from "../../data/defaultCards.json";
import defaultTicketTemplate from "../../data/defaultTicketTemplate.json";
import mdpTeamField from "../../data/mdpTeamField.json";
import MgtCss from '../mgt.module.css';
import { TEMPLATE } from "../shared/utils/constants";
import { filterListBySearch } from "../shared/utils/helper";
import { mergeStyleSets } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import _ from "lodash";

interface Props{
  templateProps: ITemplateFormProperties, 
  isUpdate?: boolean | true, 
  isMDPEnabled?: boolean | false ,
  handleSelectedCardOptionChange?: any,
  handleSelectedCardChange?: any,
}

const classNames = mergeStyleSets({
  customForms: {
    '.ac-textBlock:not(.ac-textBlock ~ .ac-textBlock)': {
      maxWidth: '90%'
    }
  }
})

export const SetDefault_CustomForm = (props: Props) => {
  const {t} = useTranslation();
  const { templateProps, isUpdate, isMDPEnabled, handleSelectedCardOptionChange,  handleSelectedCardChange} = props;
  const { templateData } = templateProps;
  const [ selectedCardIndex, setSelectedCardIndex ] = useState(null);
  const [ selectedOption, setSelectedOption ] = useState("scratch");
  const [ cards, setCards ] = useState(defaultCards);
  const [ defaultTicketCard, setDefaultTicketCard ] = useState({});

  useEffect(() => {
    if(templateData.IsDefault){
      const defaultTemplateReset: any = _.cloneDeep(defaultTicketTemplate);
      const mdpTeamFieldReset: any = mdpTeamField;
      if(isMDPEnabled)
        defaultTemplateReset["body"].unshift(mdpTeamFieldReset);
      templateData.CardResponseJson = JSON.stringify(defaultTemplateReset);
      setDefaultTicketCard(defaultTemplateReset);
    }else{
      templateData.CardResponseJson = "";
      setCards((currentItems) => {
        return currentItems.map((cardData) => {
          cardData["hidden"] = false;
          return cardData;
        });
      });
    }
  }, []);

  useEffect(() => {
    if(selectedOption == "default" && selectedCardIndex == null){
      templateData.errorFormsArray?.push("NoSelectedCardIndex");
      templateData.validateForms = true;
    }
    else{
      templateData.errorFormsArray = templateData.errorFormsArray?.filter(x => x != "NoSelectedCardIndex");
      templateData.validateForms = false;
    }

    handleSelectedCardChange?.(selectedCardIndex);
    handleSelectedCardOptionChange?.(selectedOption);
  }, [selectedOption, selectedCardIndex]);
  
  const cardItems = [
    {
      name: 'cardItems',
      key: 'CardItem',
      value: 'cardItem',
    }
  ];

  const handleSearch = (event: any) => {
    const target = event.target.value.toString() || "";
    setCards(() => filterListBySearch(defaultCards, target));
  };

  const onSelectCard = (checked: boolean, index: number, cardJson: any) => {
    if(!checked){
      setSelectedCardIndex(null);
      templateData.CardResponseJson = "";
    }else{
      templateData.validateForms = false;
      setSelectedCardIndex(index);
      delete cardJson["hidden"];
      templateData.CardResponseJson = JSON.stringify(cardJson);
    }
  }

  const renderItems = cards.map((cardJson, index) => (
    <>
    <Card compact ghost styles={{width: '46%', marginTop: '1rem', "&:hover": {background: 'transparent'}, height: 'fit-content'}} 
      hidden={cardJson["hidden"]} onClick={() => {
        onSelectCard(selectedCardIndex != index, index, cardJson);
      }}>
      <Card.TopControls style={{zIndex: 1}}>
      <Checkbox 
        checked={selectedCardIndex == index}
        onChange= {(e, data) => {
          onSelectCard(data.checked, index, cardJson);
        }} />
      </Card.TopControls>
      <Card.Body fitted>
        <AdaptiveCardElement keyString={"renderedCard"} jsonString={JSON.stringify(cardJson)} cardProps={{styles: {height: '350px', overflow: 'hidden', pointerEvents: 'none'}, selected: selectedCardIndex == index}} containerClassName={classNames.customForms} clear={true}/>
      </Card.Body>
    </Card>
  </>
  ));

  const chooseItems = [
    {
      name: 'templateType',
      key: 'Scratch',
      label: (isUpdate) ? TEMPLATE.START_FROM_SCRATCH : TEMPLATE.CREATE_FROM_SCRATCH,
      value: 'scratch',
    },
    {
      name: 'templateType',
      key: 'Default',
      label: (isUpdate) ? TEMPLATE.START_FROM_DEFAULT : TEMPLATE.CREATE_FROM_DEFAULT,
      value: 'default'
    }
  ];

  return (
    <ProviderConsumer render={(globalTheme) => (
        <>
          <Flex column style={{flex: 1, maxHeight: '70vh', overflow: 'auto'}} gap="gap.large">
            <Flex.Item>
              <Flex column>
              <Text content={(isUpdate) ? TEMPLATE.UPDATE_CUSTOM_FORM :TEMPLATE.CREATE_CUSTOM_FORM} weight="bold" size="large" />
              <Text content={(isUpdate) ? templateData.IsDefault ? TEMPLATE.DEFAULT_CUSTOM_FORM_DESCRIPTION : TEMPLATE.UPDATE_CUSTOM_FORM_DESCRIPTION: TEMPLATE.CREATE_CUSTOM_FORM_DESCRIPTION}  />
              </Flex>
            </Flex.Item>
            {!templateData.IsDefault && (
            <>
              <Flex.Item>
                <Flex column gap="gap.small">
                  <RadioGroup
                    vertical
                    items={chooseItems}
                    defaultCheckedValue={selectedOption}
                    onCheckedValueChange={(e, data) => {
                        setSelectedOption(data["value"].toString())
                        templateData.NoCustomForm = false;
                        if(data["value"].toString() == "scratch"){
                          setSelectedCardIndex(null);
                          templateData.CardResponseJson = "";
                        } 
                        else if (data["value"].toString() == "noCustomForm"){
                          setSelectedCardIndex(null);
                          templateData.NoCustomForm = true;
                          templateData.CardResponseJson = "";
                        }
                      }
                    }
                  />
                </Flex>
              </Flex.Item>
              <Flex.Item>
                <div>
                <Flex vAlign="center" {...(selectedOption != "default") && ({className: MgtCss.disabledContainer})}>
                  <Text content={TEMPLATE.DEFAULT_FORMS} weight="bold" />
                  <Flex.Item push>
                    <Input icon={<SearchIcon />} placeholder={t('template.search')} onChange={handleSearch}/>
                  </Flex.Item>
                </Flex>
                </div>
              </Flex.Item>
              
              <Flex.Item>
                <Flex gap="gap.medium" className={MgtCss.cardSelectionContainer}
                  {...(selectedOption != "default") && ({className: `${MgtCss.cardSelectionContainer} ${MgtCss.disabledContainer}`})}>
                  {renderItems}
                </Flex>
              </Flex.Item>
            </>
            )}
            {templateData.IsDefault && (
              <Flex.Item>
                <Flex gap="gap.medium" className={MgtCss.cardSelectionContainer} >
                <Card compact ghost styles={{width: '100%', marginTop: '1rem', "&:hover": {background: 'transparent'}, height: 'fit-content'}} 
                  >
                  <Card.Body fitted>
                    <AdaptiveCardElement showDefaultCardButtons keyString={"renderedCard"} jsonString={JSON.stringify(defaultTicketCard)} cardProps={{styles: {height: '350px', overflow: 'hidden', pointerEvents: 'none'}}} containerClassName={classNames.customForms} clear={true}/>
                  </Card.Body>
                </Card>
                </Flex>
              </Flex.Item>
            )}
          </Flex>
        
        </>
    )} />
  )
}